<template>
  <v-btn color="success" :loading="exporting" @click="exportTemplate">
    <v-icon left>mdi-file-excel</v-icon>
    Export
  </v-btn>
</template>

<script>
// Install dependency: npm install xlsx-js-style
import XLSXStyle from 'xlsx-js-style'

export default {
  name: 'ExportPaymentTemplate',

  props: {
    // Array of objects with keys:
    // order_number, SQL_export_code, formatted_date, SQL_payment_code, amount
    data: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      exporting: false,
    }
  },

  methods: {
    exportTemplate() {
      this.exporting = true
      try {
        const wb = XLSXStyle.utils.book_new()
        const ws = this.buildSheet()
        XLSXStyle.utils.book_append_sheet(wb, ws, 'ARAP Payment')
        const wbout = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' })
        this.downloadFile(wbout, 'Import_Customer_Payment.xlsx')
      } finally {
        this.exporting = false
      }
    },

    buildSheet() {
      const ws = {}

      // ── Shared style helpers ──────────────────────────────────────────────
      const thinBorder = {
        top:    { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left:   { style: 'thin', color: { rgb: '000000' } },
        right:  { style: 'thin', color: { rgb: '000000' } },
      }

      const greenFill  = { patternType: 'solid', fgColor: { rgb: 'C5E0B4' } }
      const yellowFill = { patternType: 'solid', fgColor: { rgb: 'FFFF00' } }
      const baseFont   = { name: 'Calibri', sz: 11 }

      // Helper: write a cell
      // Note: always use 's' or 'n' — never 'z', as xlsx-js-style skips styling on empty/z cells
      const C = (addr, value, style) => {
        const isEmpty = value === null || value === undefined
        const t = (!isEmpty && typeof value === 'number') ? 'n' : 's'
        ws[addr] = { v: isEmpty ? '' : value, t, s: style }
      }

      // ── ROW 1: Color Key legend ───────────────────────────────────────────
      C('A1', 'Color Key', {
        font:      { ...baseFont, bold: true },
        alignment: { vertical: 'top' },
        border:    thinBorder,
      })
      C('B1', 'Note', {
        font:      baseFont,
        fill:      greenFill,
        alignment: { vertical: 'bottom' },
        border:    thinBorder,
      })
      C('C1', 'Required', {
        font:      baseFont,
        fill:      yellowFill,
        alignment: { vertical: 'center' },
        border:    thinBorder,
      })

      // ── ROW 3: Notes / hints (green background across A3:S3) ─────────────
      const allCols = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S']

      allCols.forEach(col => {
        C(`${col}3`, null, {
          font:      baseFont,
          fill:      greenFill,
          alignment: { vertical: 'center', wrapText: true },
        })
      })

      // Cells with actual note text
      C('C3', 'DD/MM/YYYY', {
        font:      baseFont,
        fill:      greenFill,
        alignment: { vertical: 'center', wrapText: true },
      })
      C('N3', 'F = False\nT= True', {
        font:      baseFont,
        fill:      greenFill,
        alignment: { vertical: 'center', wrapText: true },
      })
      C('O3', 'F = False\nT= True', {
        font:      baseFont,
        fill:      greenFill,
        alignment: { vertical: 'center', wrapText: true },
      })
      C('Q3', 'IV = Invoice\nDN = Debit Note\nPI = Supplier IV\nSD = Supplier DN', {
        font:      baseFont,
        fill:      greenFill,
        alignment: { vertical: 'center', wrapText: true },
      })
      C('R3', 'Doc No to be knocked off', {
        font:      baseFont,
        fill:      greenFill,
        alignment: { vertical: 'center', wrapText: true },
      })

      // ── ROW 5: Header row ─────────────────────────────────────────────────
      const headers = [
        { col: 'A', label: 'DOCNO(20)',           required: true  },
        { col: 'B', label: 'CODE(10)',             required: true  },
        { col: 'C', label: 'DOCDATE',              required: true  },
        { col: 'D', label: 'AREA(10)',             required: false },
        { col: 'E', label: 'AGENT(10)',            required: false },
        { col: 'F', label: 'DESCRIPTION(200)',     required: false },
        { col: 'G', label: 'PAYMENTMETHOD(10)',    required: true  },
        { col: 'H', label: 'CHEQUENUMBER(20)',     required: false },
        { col: 'I', label: 'PROJECT(20)',          required: false },
        { col: 'J', label: 'PAYMENTPROJECT(20)',   required: false },
        { col: 'K', label: 'CURRENCYRATE',         required: false },
        { col: 'L', label: 'BANKCHARGE',           required: false },
        { col: 'M', label: 'DOCAMT',               required: true  },
        { col: 'N', label: 'CANCELLED(1)',         required: false },
        { col: 'O', label: 'NONREFUNDABLE',        required: false },
        { col: 'P', label: 'BOUNCEDDATE',          required: false },
        { col: 'Q', label: '_DOCTYPE',             required: false },
        { col: 'R', label: '_DOCNO',               required: false },
        { col: 'S', label: '_KOAMT',               required: false },
      ]

      headers.forEach(({ col, label, required }) => {
        C(`${col}5`, label, {
          font:      { ...baseFont, bold: true },
          fill:      required ? yellowFill : undefined,
          alignment: { horizontal: 'center', vertical: 'center' },
          border:    thinBorder,
        })
      })

      // ── ROWS 6+: Dynamic data rows from prop ─────────────────────────────
      const dataStyle = (halign = 'general') => ({
        font:      baseFont,
        alignment: { horizontal: halign, vertical: 'bottom' },
        border:    thinBorder,
      })

      const rows = this.data.length ? this.data : []
      rows.forEach((item, index) => {
        const r = index + 6 // data starts at row 6
        C(`A${r}`, 'IV-'+item.order_number || '---', dataStyle())
        C(`B${r}`, item.SQL_export_code   || '---', dataStyle())
        C(`C${r}`, item.formatted_date    || '---', dataStyle('right'))
        C(`D${r}`, '----',                          dataStyle())
        C(`E${r}`, '----',                          dataStyle())
        C(`F${r}`, '----',                          dataStyle())
        C(`G${r}`, item.SQL_payment_code  || '---', dataStyle())
        C(`H${r}`, '----',                          dataStyle())
        C(`I${r}`, '----',                          dataStyle())
        C(`J${r}`, '----',                          dataStyle())
        C(`K${r}`, 1,                               dataStyle('right'))
        C(`L${r}`, 0,                               dataStyle('right'))
        C(`M${r}`, parseFloat(item.amount) || 0,    dataStyle('right'))
        C(`N${r}`, 'F',                             dataStyle())
        C(`O${r}`, 'F',                             dataStyle())
        C(`P${r}`, '',                              dataStyle())
        C(`Q${r}`, '',                              dataStyle())
        C(`R${r}`, '',                              dataStyle())
        C(`S${r}`, '',                              dataStyle('right'))
      })

      const lastRow = Math.max(rows.length + 5, 5)
      ws['!ref'] = `A1:S${lastRow}`

      // ── Column widths (matching original) ─────────────────────────────────
      ws['!cols'] = [
        { wch: 11.71 }, // A
        { wch: 12.29 }, // B
        { wch: 13.43 }, // C
        { wch: 9.71  }, // D
        { wch: 11.14 }, // E
        { wch: 18.71 }, // F
        { wch: 22.86 }, // G
        { wch: 21.71 }, // H
        { wch: 13.14 }, // I
        { wch: 22.86 }, // J
        { wch: 16.86 }, // K
        { wch: 14.71 }, // L
        { wch: 21.71 }, // M
        { wch: 15.43 }, // N
        { wch: 18.71 }, // O
        { wch: 15.86 }, // P
        { wch: 17.71 }, // Q
        { wch: 22.57 }, // R
        { wch: 16.71 }, // S
      ]

      // ── Row heights ───────────────────────────────────────────────────────
      const rowHeights = [
        { hpt: 15    }, // Row 1
        {},             // Row 2
        { hpt: 54.75 }, // Row 3 (tall for multi-line notes)
        {},             // Row 4
        { hpt: 16.5  }, // Row 5 header
      ]
      rows.forEach(() => rowHeights.push({ hpt: 16.5 }))
      ws['!rows'] = rowHeights

      return ws
    },

    downloadFile(buffer, filename) {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a   = document.createElement('a')
      a.href     = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>